import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious,
} from "@/components/ui/carousel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  HeartPulse, Activity, ScanLine, Share2, ShieldCheck, ArrowRight, Check, Lock,
  Mail, ChevronRight, MessageCircle, Sparkles, Crown,
} from "lucide-react";

const SUPPORT_EMAIL = "care@verdahealth.com";

const SLIDES = [
  {
    eyebrow: "Daily monitoring",
    title: "Monitor your health & symptoms",
    body: "Log vitals like BP, sugar, weight, SpO₂ and heart rate. Capture every symptom with severity and duration. Ask a friendly AI in plain English when something feels off.",
    icon: Activity,
    color: "#4A6750",
    image: "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=2000&q=80&auto=format&fit=crop",
    chips: ["BP & Sugar", "AI symptom guide", "Voice input"],
  },
  {
    eyebrow: "Diagnostics & treatment",
    title: "Scan reports. Track medicines. One place.",
    body: "Point your camera at a lab report — parameters extract automatically. Scan prescriptions and Verda fills in your morning, afternoon, evening and night doses, before or after food.",
    icon: ScanLine,
    color: "#5B7582",
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=2000&q=80&auto=format&fit=crop",
    chips: ["22 diagnostic types", "Prescription OCR", "Dose schedule"],
  },
  {
    eyebrow: "Care collaboration",
    title: "Share with your health experts",
    body: "Download a clean PDF for your next consultation, or send a private link your doctor can open in a browser. Read-only. Auto-expires in 7 days.",
    icon: Share2,
    color: "#C76B58",
    image: "https://images.unsplash.com/photo-1666214280557-f1b5022eb634?w=2000&q=80&auto=format&fit=crop",
    chips: ["Doctor PDF", "Private share link", "Read-only"],
  },
  {
    eyebrow: "Privacy by design",
    title: "A clear track of health — with privacy",
    body: "Your records are yours. We redact phone numbers and home addresses from anything you share. You decide who sees what, and for how long.",
    icon: ShieldCheck,
    color: "#1C2E24",
    image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=2000&q=80&auto=format&fit=crop",
    chips: ["End-to-end private", "PII redaction on share", "Auto-expiry"],
  },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState(null); // null | "register" | "signin"
  const [authNonce, setAuthNonce] = useState(0);
  const openAuth = (mode) => { if (user) return; setAuthMode(mode); setAuthNonce(n => n + 1); };
  const goPrimary = () => (user ? navigate("/dashboard") : openAuth("register"));

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#1C2E24]">
      {/* NAV with Create account / Sign in */}
      <header className="sticky top-0 z-30 bg-[#F9F8F6]/90 backdrop-blur-md border-b border-[#E5E7E4]" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <HeartPulse className="w-6 h-6 text-[#4A6750]" strokeWidth={1.5} />
            <span className="font-heading text-lg font-medium tracking-tight">Verda Health</span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-[#5C6D63]">
            <a href="#features" className="hover:text-[#1C2E24] transition-colors" data-testid="nav-features">Features</a>
            <a href="#pricing" className="hover:text-[#1C2E24] transition-colors" data-testid="nav-pricing">Pricing</a>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-[#1C2E24] transition-colors flex items-center gap-1.5" data-testid="nav-support">
              <Mail className="w-3.5 h-3.5" /> Support
            </a>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <Button onClick={() => navigate("/dashboard")} data-testid="nav-open-dashboard"
                className="rounded-xl bg-[#4A6750] hover:bg-[#3B5340] text-white h-10">
                Open dashboard <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            ) : (
              <>
                <button onClick={() => openAuth("signin")} data-testid="nav-signin"
                  className="text-sm font-medium text-[#1C2E24] hover:text-[#4A6750] px-3 h-10 rounded-xl transition-colors">
                  Sign in
                </button>
                <Button onClick={() => openAuth("register")} data-testid="nav-register"
                  className="rounded-xl bg-[#4A6750] hover:bg-[#3B5340] text-white h-10">
                  Create account <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* FULL-WIDTH HORIZONTAL CAROUSEL HERO */}
      <FeatureCarousel onPrimary={goPrimary} primaryLabel={user ? "Open dashboard" : "Create account"} />

      {/* FEATURE STRIP */}
      <section id="features" className="py-20 lg:py-24 bg-white border-y border-[#E5E7E4]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#7D8F84]">{"What's inside"}</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight mt-3 leading-[1.05]">
              Built for the moments that actually matter.
            </h2>
            <p className="text-[#5C6D63] mt-4 leading-relaxed">
              {"We built Verda for the moment your parent calls and asks \"what was my sugar last Tuesday?\" — and you actually have the answer."}
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Activity, t: "Daily vitals", b: "BP · Sugar · Weight · Heart rate · SpO₂", c: "#5B8266" },
              { icon: Sparkles, t: "AI symptom guide", b: "Plain-English explanations, red flags & self-care", c: "#C76B58" },
              { icon: ScanLine, t: "Scan reports & rx", b: "Camera-first OCR fills your forms", c: "#5B7582" },
              { icon: Share2, t: "Share with doctor", b: "Clean PDF or private 7-day link", c: "#4A6750" },
            ].map(f => (
              <Card key={f.t} className="p-5 rounded-2xl border border-[#E5E7E4] bg-white hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`feature-${f.t}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${f.c}15` }}>
                  <f.icon className="w-5 h-5" strokeWidth={1.5} style={{ color: f.c }} />
                </div>
                <h3 className="font-heading text-lg font-medium leading-snug">{f.t}</h3>
                <p className="text-sm text-[#5C6D63] mt-1.5 leading-relaxed">{f.b}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PRIVACY BANNER */}
      <section className="py-16 lg:py-20 bg-[#1C2E24] text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#9CB29F]">Built privacy-first</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3 leading-[1.1]">
              Your records are yours. <span className="text-[#9CB29F]">Visible to whom you choose.</span>
            </h2>
            <p className="text-[#C7D0C8] mt-4 max-w-xl leading-relaxed">
              When you share with a doctor, we redact phone numbers and home addresses. Links expire in 7 days.
              You can revoke any time.
            </p>
          </div>
          <Button onClick={goPrimary} data-testid="privacy-cta"
            className="rounded-xl bg-white text-[#1C2E24] hover:bg-[#E8ECE9] h-12 px-6">
            {user ? "Open dashboard" : "Start free"} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 lg:py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-xl mx-auto">
            <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#7D8F84]">Pricing</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight mt-3 leading-[1.05]">
              Start free. <span className="text-[#4A6750]">Always.</span>
            </h2>
            <p className="text-[#5C6D63] mt-4">Pro is on its way for trends, reminders and caregiver sharing.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-12">
            <PricingCard tier="free" goPrimary={goPrimary} user={user} />
            <PricingCard tier="pro" goPrimary={goPrimary} user={user} />
          </div>
        </div>
      </section>

      {/* SUPPORT BLOCK */}
      <section className="py-14 bg-[#E8ECE9]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-[#4A6750]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-heading text-xl font-medium">Need help?</p>
              <p className="text-sm text-[#5C6D63]">A real human reads every email. Reply within one business day.</p>
            </div>
          </div>
          <a href={`mailto:${SUPPORT_EMAIL}?subject=Verda%20Health%20support`}
             data-testid="support-cta"
             className="inline-flex items-center gap-2 rounded-xl bg-[#1C2E24] text-white hover:bg-[#0F1A14] px-5 h-11 text-sm font-medium transition-colors">
            <Mail className="w-4 h-4" /> {SUPPORT_EMAIL}
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E5E7E4] py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#7D8F84]">
          <div className="flex items-center gap-2">
            <HeartPulse className="w-4 h-4 text-[#4A6750]" strokeWidth={1.5} />
            <span className="font-heading font-medium text-[#1C2E24]">Verda Health</span>
            <span>· © {new Date().getFullYear()}</span>
          </div>
          <div className="flex items-center gap-5">
            <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-[#1C2E24] transition-colors flex items-center gap-1.5" data-testid="footer-support">
              <Mail className="w-3.5 h-3.5" /> {SUPPORT_EMAIL}
            </a>
            <a href="/admin/login" className="hover:text-[#1C2E24] transition-colors" data-testid="footer-admin-login">Admin</a>
            <span className="hidden sm:inline">Patient-generated · Not a medical diagnosis</span>
          </div>
        </div>
      </footer>

      {/* AUTH DIALOG — remount per-open so internal state resets cleanly */}
      {authMode && (
        <AuthDialog
          key={`auth-${authMode}-${authNonce}`}
          open
          initialMode={authMode}
          onOpenChange={(o) => !o && setAuthMode(null)}
        />
      )}
    </div>
  );
}

/* ---------------- Full-width horizontal carousel ---------------- */
function FeatureCarousel({ onPrimary, primaryLabel }) {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    const id = setInterval(() => { if (api) api.scrollNext(); }, 7000);
    return () => { api.off("select", onSelect); clearInterval(id); };
  }, [api]);

  return (
    <section className="relative" data-testid="feature-carousel" aria-label="Key features">
      <Carousel setApi={setApi} opts={{ loop: true }} className="overflow-hidden">
        <CarouselContent className="-ml-0">
          {SLIDES.map((s, i) => (
            <CarouselItem key={s.title} className="pl-0 basis-full">
              <article className="relative w-full h-[78vh] min-h-[520px] max-h-[820px] overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${s.image})` }} aria-hidden />
                <div className="absolute inset-0 bg-gradient-to-r from-[#1C2E24]/85 via-[#1C2E24]/55 to-[#1C2E24]/10" aria-hidden />
                <div className="absolute inset-0 flex items-center">
                  <div className="max-w-7xl mx-auto w-full px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8 items-center">
                    <div className="text-white fade-in" key={s.title /* re-trigger fade per slide */}>
                      <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5">
                        <s.icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                        <span className="text-[10px] font-semibold tracking-[0.18em] uppercase">{s.eyebrow}</span>
                      </div>
                      <h1 className="!text-white font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mt-6 max-w-3xl drop-shadow-[0_2px_12px_rgba(0,0,0,0.35)]">
                        {s.title}
                      </h1>
                      <p className="text-base lg:text-lg text-white/85 mt-5 leading-relaxed max-w-2xl">{s.body}</p>
                      <div className="flex flex-wrap gap-2 mt-7">
                        {s.chips.map(c => (
                          <span key={c} className="inline-flex items-center gap-1.5 text-xs sm:text-sm px-3 py-1.5 rounded-full bg-white/12 backdrop-blur-sm text-white border border-white/20 font-medium">
                            <Check className="w-3.5 h-3.5" strokeWidth={2.5} /> {c}
                          </span>
                        ))}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-8">
                        <Button onClick={onPrimary} data-testid="hero-cta-primary"
                          className="rounded-xl bg-white text-[#1C2E24] hover:bg-[#E8ECE9] h-12 px-7 text-base font-medium">
                          {primaryLabel} <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                        <a href="#features" data-testid="hero-cta-secondary"
                          className="text-sm font-medium text-white/90 hover:text-white inline-flex items-center gap-1 px-3 py-3 transition-colors">
                          {"See what's inside"} <ChevronRight className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Slide indicator */}
                <div className="absolute bottom-6 left-0 right-0 flex items-center justify-center gap-3 z-10">
                  <span className="text-xs text-white/75 font-mono tabular-nums">{String(i + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}</span>
                </div>
              </article>
            </CarouselItem>
          ))}
        </CarouselContent>

        <CarouselPrevious data-testid="carousel-prev"
          className="left-4 lg:left-6 bg-white/15 backdrop-blur-md border-white/30 text-white hover:bg-white/25 hover:text-white w-11 h-11" />
        <CarouselNext data-testid="carousel-next"
          className="right-4 lg:right-6 bg-white/15 backdrop-blur-md border-white/30 text-white hover:bg-white/25 hover:text-white w-11 h-11" />
      </Carousel>

      {/* Dot indicators */}
      <div className="absolute bottom-14 left-0 right-0 flex items-center justify-center gap-2 z-10">
        {SLIDES.map((s, i) => (
          <button key={s.title} onClick={() => api && api.scrollTo(i)}
            data-testid={`carousel-dot-${i}`}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${
              i === current ? "w-10 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}

/* ---------------- Auth Dialog ---------------- */
const CONSENT_PURPOSES = {
  account_creation: { label: "Create & operate my account", required: true,
    description: "Required to sign you in and keep your profile." },
  health_records: { label: "Store my health records", required: true,
    description: "Vitals, symptoms, diagnostics, consultations and medications." },
  telemedicine: { label: "Telemedicine consultation", required: false,
    description: "Share records when you request a doctor consultation." },
  marketing: { label: "Marketing messages", required: false,
    description: "Wellness tips, product updates and promotions." },
  partner_sharing: { label: "Sharing data with partners", required: false,
    description: "Share de-identified data with research or insurance partners." },
  analytics: { label: "Analytics & personalization", required: false,
    description: "Improve the product and personalize your experience." },
};

function AuthDialog({ open, onOpenChange, initialMode }) {
  const mode = initialMode || "register";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-md p-0 overflow-hidden" data-testid="auth-dialog">
        <div className="p-8">
          <DialogHeader>
            <DialogTitle className="sr-only">{mode === "register" ? "Create account" : "Sign in"}</DialogTitle>
          </DialogHeader>

          <h3 className="font-heading text-2xl font-semibold leading-tight" data-testid="auth-title">
            {mode === "register" ? "Create your account" : "Welcome back"}
          </h3>
          <p className="text-sm text-[#5C6D63] mt-2">
            {mode === "register"
              ? "One click with Google. No passwords, no OTPs to remember."
              : "Sign in with the Google account you used to register."}
          </p>

          <GoogleButton mode={mode} />

          <p className="text-[11px] text-[#7D8F84] mt-6 leading-relaxed">
            By continuing you agree to store health records under India&apos;s DPDP Act 2023. Withdraw consent or delete your account any time from Privacy &amp; Data.
            <br />
            Need help? <a href={`mailto:${SUPPORT_EMAIL}`} className="underline hover:text-[#1C2E24]">{SUPPORT_EMAIL}</a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GoogleButton({ mode }) {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const go = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button onClick={go} data-testid="google-signin-btn"
      className="w-full h-12 rounded-xl bg-white hover:bg-[#F9F8F6] text-[#1C2E24] border border-[#E5E7E4] font-medium mt-5">
      <GoogleIcon /> <span className="ml-2">{mode === "register" ? "Continue with Google" : "Sign in with Google"}</span>
    </Button>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.96l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}

/* ---------------- Pricing ---------------- */
function PricingCard({ tier, goPrimary, user }) {
  if (tier === "free") {
    const free = ["Calendar with day/month/year views", "Self + 1 family profile",
      "Vitals, symptoms, diagnostics, consultations & meds", "AI symptom search",
      "Prescription & report OCR", "Voice & AI field help"];
    return (
      <Card className="p-8 rounded-2xl border-2 border-[#4A6750] bg-white relative" data-testid="pricing-free">
        <span className="absolute -top-3 left-8 text-xs font-semibold tracking-[0.15em] uppercase bg-[#4A6750] text-white px-3 py-1 rounded-full">
          Available today
        </span>
        <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#7D8F84]">Base</p>
        <h3 className="font-heading text-3xl font-semibold mt-1">Free</h3>
        <div className="my-6"><span className="font-heading text-3xl font-semibold text-[#5C6D63]">No account cost</span><p className="text-xs text-[#7D8F84] mt-2">Log daily health without a subscription.</p></div>
        <ul className="space-y-2.5">
          {free.map(f => <li key={f} className="flex items-start gap-2.5 text-sm">
            <Check className="w-4 h-4 text-[#5B8266] flex-shrink-0 mt-0.5" strokeWidth={2.25} />{f}
          </li>)}
        </ul>
        <Button onClick={goPrimary} data-testid="pricing-free-cta"
          className="w-full mt-6 rounded-xl bg-[#4A6750] hover:bg-[#3B5340] h-11">
          {user ? "Open dashboard" : "Start free"}
        </Button>
      </Card>
    );
  }
  const pro = ["Share with doctor — secure PDF + private link", "Unlimited family profiles",
    "Trend charts (BP, sugar, weight over time)", "Medication reminders",
    "Priority AI responses", "Export full archive (PDF/CSV)"];
  return (
    <Card className="p-8 rounded-2xl border-2 border-[#E5E7E4] bg-white/60 relative" data-testid="pricing-pro">
      <span className="absolute -top-3 left-8 text-xs font-semibold tracking-[0.15em] uppercase bg-[#C76B58]/10 text-[#C76B58] px-3 py-1 rounded-full">
        Coming soon
      </span>
      <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#7D8F84]">Premium</p>
      <h3 className="font-heading text-3xl font-semibold mt-1 flex items-center gap-2">
        Pro <Crown className="w-6 h-6 text-[#C76B58]" strokeWidth={1.5} />
      </h3>
      <div className="my-6"><span className="font-heading text-3xl font-semibold text-[#5C6D63]">Special early-access</span><p className="text-xs text-[#7D8F84] mt-2">Priority invite when we open Pro.</p></div>
      <ul className="space-y-2.5">
        {pro.map(f => <li key={f} className="flex items-start gap-2.5 text-sm">
          <Check className="w-4 h-4 text-[#5B8266] flex-shrink-0 mt-0.5" strokeWidth={2.25} />{f}
        </li>)}
      </ul>
      <Button disabled data-testid="pricing-pro-cta"
        className="w-full mt-6 rounded-xl bg-[#1C2E24]/80 text-white h-11">
        <Lock className="w-4 h-4 mr-2" /> Notify me
      </Button>
    </Card>
  );
}
